import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import ErrorMessage from "../component/customComponent/customMessages/ErrorMessage";

export const useDebouncedSearch = ({ searchAPI, errorMessage, delay = 500 }) => {
  const [results, setResults] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  const searchAPIRef = useRef(searchAPI);
  const errorMessageRef = useRef(errorMessage);
  const isMountedRef = useRef(true);

  useEffect(() => {
    searchAPIRef.current = searchAPI;
  }, [searchAPI]);

  useEffect(() => {
    errorMessageRef.current = errorMessage;
  }, [errorMessage]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const fetchResults = useMemo(
    () =>
      debounce(async (text) => {
        try {
          setIsFetching(true);
          const { data } = await searchAPIRef.current(text);
          // The awaited request can resolve after unmount; the debounce cancel only
          // drops pending calls, not an in-flight one, so guard the state updates.
          if (!isMountedRef.current) return;
          setResults(data?.data ?? []);
        } catch {
          ErrorMessage(errorMessageRef.current);
        } finally {
          if (isMountedRef.current) setIsFetching(false);
        }
      }, delay),
    [delay]
  );

  useEffect(() => () => fetchResults.cancel(), [fetchResults]);

  const onSearch = (text) => {
    setSearchText(text);
    if (text?.trim()) {
      fetchResults(text);
    } else {
      // Clearing the input: drop any pending fetch and stop the spinner so it doesn't
      // linger on a request the user no longer cares about.
      fetchResults.cancel();
      setIsFetching(false);
    }
  };

  return { results, setResults, searchText, setSearchText, isFetching, onSearch };
};
